/**
 * Sales methodology field definitions used by deal drill-down cards.
 *
 * When a workspace selects one of these methodologies as their summary
 * template (Settings → Intelligence), the matching fields appear on the
 * deal drill-down card so reps can capture and review the data inline.
 */

import enMethodology from "@/lib/i18n/locales/en/methodology.json";

/**
 * `displayName` is an IDENTIFIER, not a display label.
 *
 * `resolveMethodologyCrmName` below matches it against the workspace's own
 * `crm_label` to find the real custom field a methodology row reads and writes,
 * and `seed-resolvers.ts` matches it against what the user typed. Translating
 * it would break both, so it stays English and the DISPLAY path is separate —
 * `methodologyFieldLabel` / `methodologyFieldDescription`, keyed by the stable
 * `fieldName`. Same split as `src/lib/grid/column-label.ts`.
 */
export type MethodologyField = {
  fieldName: string;
  displayName: string;
  description: string;
};

export const METHODOLOGY_FIELDS: Record<string, MethodologyField[]> = {
  bant: [
    { fieldName: "bant_budget", displayName: "Budget", description: "Budget or financial constraints discussed" },
    { fieldName: "bant_authority", displayName: "Authority", description: "Decision-makers and approval process" },
    { fieldName: "bant_need", displayName: "Need", description: "Pain points or needs identified" },
    { fieldName: "bant_timeline", displayName: "Timeline", description: "Timeline or deadlines mentioned" },
  ],
  meddic: [
    { fieldName: "meddic_metrics", displayName: "Metrics", description: "Quantifiable measures of success" },
    { fieldName: "meddic_economic_buyer", displayName: "Economic Buyer", description: "Person who controls budget and final purchasing decision" },
    { fieldName: "meddic_decision_criteria", displayName: "Decision Criteria", description: "Criteria used to evaluate solutions" },
    { fieldName: "meddic_decision_process", displayName: "Decision Process", description: "Decision-making process and timeline" },
    { fieldName: "meddic_identify_pain", displayName: "Identify Pain", description: "Key pain points identified" },
    { fieldName: "meddic_champion", displayName: "Champion", description: "Internal champion advocating for the solution" },
  ],
  meddpicc: [
    { fieldName: "meddpicc_metrics", displayName: "Metrics", description: "Quantifiable measures of success" },
    { fieldName: "meddpicc_economic_buyer", displayName: "Economic Buyer", description: "Person who controls budget and final purchasing decision" },
    { fieldName: "meddpicc_decision_criteria", displayName: "Decision Criteria", description: "Criteria used to evaluate solutions" },
    { fieldName: "meddpicc_decision_process", displayName: "Decision Process", description: "Decision-making process and timeline" },
    { fieldName: "meddpicc_paper_process", displayName: "Paper Process", description: "Legal, procurement, or paperwork process" },
    { fieldName: "meddpicc_identify_pain", displayName: "Identify Pain", description: "Key pain points identified" },
    { fieldName: "meddpicc_champion", displayName: "Champion", description: "Internal champion advocating for the solution" },
    { fieldName: "meddpicc_competition", displayName: "Competition", description: "Competitors or alternatives mentioned" },
  ],
  spiced: [
    { fieldName: "spiced_situation", displayName: "Situation", description: "Current situation or context" },
    { fieldName: "spiced_pain", displayName: "Pain", description: "Key pain points discussed" },
    { fieldName: "spiced_impact", displayName: "Impact", description: "Impact of the pain on the business" },
    { fieldName: "spiced_critical_event", displayName: "Critical Event", description: "Critical events or deadlines driving urgency" },
    { fieldName: "spiced_decision", displayName: "Decision", description: "How the decision will be made and by whom" },
  ],
};

/**
 * The methodology acronyms. Never translated (GLOSSARY.md rule 2), and also
 * matched verbatim by `normalizeLabel` below and by the backend's built-in
 * template names (`methodology-scope.ts`).
 */
export const METHODOLOGY_LABELS: Record<string, string> = {
  bant: "BANT",
  meddic: "MEDDIC",
  meddpicc: "MEDDPICC",
  spiced: "SPICED",
};

/** The `t` from `useTranslation()`, narrowed to what this module calls. */
export type MethodologyTranslator = (key: string, options?: Record<string, unknown>) => string;

/**
 * The display label for one methodology field row.
 *
 * WHY THIS USES `defaultValue` (the same deliberate exception `columnLabel`
 * makes): the row renderer that draws methodology fields also draws the
 * workspace's OWN deal custom fields, whose `displayName` is a label a user
 * typed and must never be translated. Those arrive with a `fieldName` that is a
 * `crm_name` and so has no catalog entry, and the fallback is what makes them
 * render verbatim. `methodology-fields.test.ts` is what stops a REAL
 * methodology field quietly relying on it: every `fieldName` in
 * `METHODOLOGY_FIELDS` must exist in `locales/en/methodology.json`.
 */
export function methodologyFieldLabel(
  field: Pick<MethodologyField, "fieldName" | "displayName">,
  t: MethodologyTranslator,
): string {
  return t(`methodology.fields.${field.fieldName}.label`, { defaultValue: field.displayName });
}

/** The description under a methodology field row. Empty for custom fields. */
export function methodologyFieldDescription(
  field: Pick<MethodologyField, "fieldName" | "description">,
  t: MethodologyTranslator,
): string {
  if (!field.description) return "";
  return t(`methodology.fields.${field.fieldName}.description`, {
    defaultValue: field.description,
  });
}

/**
 * Methodology field names with no entry in `locales/en/methodology.json`.
 * Drives the parity test: a field added here without a catalog entry would
 * silently fall back to English in every language.
 */
export function unlabeledMethodologyFieldNames(): string[] {
  const catalog = (enMethodology as { fields: Record<string, { label?: string; description?: string }> }).fields;
  return Object.values(METHODOLOGY_FIELDS)
    .flat()
    .filter(f => !catalog[f.fieldName]?.label || !catalog[f.fieldName]?.description)
    .map(f => f.fieldName)
    .sort();
}

export function isMethodologyTemplate(templateId: string | null | undefined): templateId is keyof typeof METHODOLOGY_FIELDS {
  return !!templateId && templateId in METHODOLOGY_FIELDS;
}

const METHODOLOGY_PREFIXES = ["bant_", "meddic_", "meddpicc_", "spiced_"];

/** Strip the methodology prefix (e.g. "meddpicc_metrics" -> "metrics"). */
export function methodologyBaseName(fieldName: string): string {
  const prefix = METHODOLOGY_PREFIXES.find(p => fieldName.startsWith(p));
  return prefix ? fieldName.slice(prefix.length) : fieldName;
}

/**
 * Resolve a hardcoded methodology field to the workspace's ACTUAL deal custom
 * field crm_name, so the detail sheet reads/writes the same field the grid and
 * CRM sync use (e.g. `metrics_5876_coffee`) rather than the synthetic
 * `meddpicc_metrics` key, which no workspace actually stores.
 *
 * Match order:
 *  1) crm_label === methodology display name (labels align by construction —
 *     both originate from the methodology field's display name);
 *  2) crm_name matches `<base>_<wksp>_coffee` (handles a label mismatch but a
 *     conventional crm_name).
 * Returns null when neither matches, so callers fall back to the generic key.
 */
export function resolveMethodologyCrmName(
  field: { fieldName: string; displayName: string },
  customFields: ReadonlyArray<{ crmName: string; label: string }>,
): string | null {
  const wanted = field.displayName.trim().toLowerCase();
  // A label may carry the methodology as a prefix ("MEDDIC: Metrics", "MEDDIC Metrics",
  // "MEDDIC - Metrics"): strip it before comparing, so a hand-made field set still resolves.
  const acronyms = Object.values(METHODOLOGY_LABELS).map(a => a.toLowerCase());
  const normalizeLabel = (label: string) => {
    const lower = label.trim().toLowerCase();
    for (const a of acronyms) {
      const m = lower.match(new RegExp(`^${a}\\s*[:\\-–]?\\s*(.+)$`));
      if (m) return m[1].trim();
    }
    return lower;
  };
  const byLabel = customFields.find(cf => normalizeLabel(cf.label ?? "") === wanted);
  if (byLabel) return byLabel.crmName;

  // `<base>_<ws>_coffee` is the convention Settings → Intelligence creates; a field created by
  // hand as `<methodology>_<base>_<ws>_coffee` is the same field with the prefix kept.
  const base = methodologyBaseName(field.fieldName);
  const re = new RegExp(`^(?:${base}|${field.fieldName})_\\d+_coffee$`);
  const bySuffix = customFields.find(cf => re.test(cf.crmName));
  return bySuffix ? bySuffix.crmName : null;
}

/**
 * Storage key for the workspace's chosen summary template.
 * Mirrors the key used in SettingsPage so both reads stay in sync.
 */
export function summaryTemplateStorageKey(workspaceId: string | null | undefined): string {
  const suffix = workspaceId ? `:ws:${workspaceId}` : "";
  return `settings:summary-template${suffix}`;
}